import { Button, Stack } from '@mui/joy';

import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

import { StructuredPromptSchema } from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';
import { Tooltip } from '@/design-system';

import { useLanguagePreference } from '../../hooks/useLanguagePreferenceContext/useLanguagePreference';
import { CopyIconButton } from '../CopyButton/CopyButton';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { EditorModeSelect } from '../ExampleCrudPane/EditInputFunctions';
import { isJsonSchema7 } from './JsonSchema.guard';
import { SchemaEditor } from './SchemaEditor';
import { SchemaEditorVariant } from './types';

type SchemaEditorModalBodyProps = {
  value: StructuredPromptSchema;
  setValue: Dispatch<SetStateAction<StructuredPromptSchema>>;
  onSubmit?: (value: StructuredPromptSchema) => void;
  onRemove?: () => void;
  variant?: SchemaEditorVariant;
  readOnly: boolean;
  onClose?: () => void;
  saveDisabled?: boolean;
  saveTooltipText?: string;
  error?: string;
  title?: ReactNode;
};

const SchemaEditorModalBody: FC<SchemaEditorModalBodyProps> = ({
  value,
  setValue,
  onSubmit,
  onRemove,
  variant = SchemaEditorVariant.FUNCTIONS,
  readOnly,
  onClose,
  saveDisabled,
  saveTooltipText,
  error,
  title,
}) => {
  const [schemaError, setSchemaError] = useState<string | undefined>();

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);

  const schemaScrollRef = useRef<HTMLDivElement>(null);

  const isEditorAvailable = isJsonSchema7(value) && value.type === 'object';

  const [isEditor, setIsEditor] = useState(isEditorAvailable);
  const [language, setLanguage] = useLanguagePreference();

  useEffect(() => {
    setIsEditor(isEditorAvailable);
  }, [isEditorAvailable]);

  const mode = isEditor && isEditorAvailable ? ('editor' as const) : language;

  return (
    <>
      <div className="flex flex-row items-center justify-between">
        {title}
        <div className="ml-auto flex flex-row gap-2">
          <EditorModeSelect
            mode={mode}
            isEditor={isEditor}
            setIsEditor={setIsEditor}
            language={language}
            setLanguage={setLanguage}
            readOnly={readOnly}
            isEditorAvailable={isEditorAvailable}
          />
          <CopyIconButton
            copy={JSON.stringify(value, null, 2)}
            className="p-2"
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 overflow-hidden">
        <div className="overflow-y-auto pr-2" ref={schemaScrollRef}>
          <SchemaEditor
            value={value}
            onChange={setValue}
            isEditor={isEditor}
            language={language}
            variant={variant}
            readOnly={readOnly}
            schemaScrollRef={schemaScrollRef}
            setSchemaError={setSchemaError}
            schemaError={schemaError || error}
          />
        </div>
        {(schemaError || error) && !isEditor && (
          <div className="text-xs text-error">{schemaError || error}</div>
        )}
        <div className="mt-2 flex w-full flex-row justify-between">
          {onRemove && (
            <Button
              type="button"
              data-testid="schema-modal-remove-button"
              size="sm"
              color="danger"
              variant="outlined"
              onClick={() => setShowRemoveConfirmation(true)}
            >
              Delete
            </Button>
          )}
          <Stack direction="row" gap={1} className="ml-auto">
            <Button
              type="button"
              data-testid="schema-modal-cancel-button"
              size="sm"
              color="neutral"
              variant="plain"
              sx={{ fontWeight: '500' }}
              onClick={onClose}
            >
              Cancel
            </Button>
            {onSubmit && (
              <Tooltip title={schemaError || error || saveTooltipText}>
                <div>
                  <Button
                    type="button"
                    data-testid="schema-modal-save-button"
                    size="sm"
                    color="primary"
                    variant="solid"
                    sx={{ fontWeight: '500' }}
                    onClick={() => onSubmit(value)}
                    disabled={!!schemaError || !!error || saveDisabled}
                  >
                    Save changes
                  </Button>
                </div>
              </Tooltip>
            )}
          </Stack>
        </div>
      </div>
      {showRemoveConfirmation && onRemove && (
        <DeleteConfirmationModal
          isOpen={showRemoveConfirmation}
          onClose={() => setShowRemoveConfirmation(false)}
          onConfirm={onRemove}
          title={`Delete ${value.title ?? value.type}?`}
          description={`Are you sure you want to delete ${
            value.title ?? value.type
          }? This action cannot be undone.`}
        />
      )}
    </>
  );
};

export { SchemaEditorModalBody };
